import { TFunction } from 'i18next';
import type { MatrixClient, Room, RoomMember } from 'matrix-js-sdk/src/matrix';

import { getRoomName, getUserSetRoomName, isSystemUser } from 'utils/matrixUtils';

export type ChatDisplayNameVariant = 'user-set-name' | 'peer-name' | 'member-list' | 'current-user' | 'fallback';

export type ChatDisplayName = {
  /** Full untruncated string — safe for search, alt text, and screen readers. */
  full: string;
  /** Participant names when the label is a comma-joined list. Empty for other variants. */
  names: string[];
  /** Which variation produced the label. */
  variant: ChatDisplayNameVariant;
};

const localpartFromUserId = (userId: string): string => {
  const local = userId.startsWith('@') ? userId.slice(1) : userId;
  const colon = local.indexOf(':');
  return colon >= 0 ? local.slice(0, colon) : local;
};

const memberDisplayName = (member: Pick<RoomMember, 'rawDisplayName' | 'name' | 'userId'>): string => {
  const raw = member.rawDisplayName?.trim();
  if (raw) return raw;
  const name = member.name?.trim();
  if (name) return name;
  return localpartFromUserId(member.userId);
};

const fallback = (room: Room): ChatDisplayName => ({
  full: getRoomName(room),
  names: [],
  variant: 'fallback',
});

export const getChatDisplayName = (room: Room, matrixClient: MatrixClient | null, _t: TFunction): ChatDisplayName => {
  const userSetName = getUserSetRoomName(room);
  if (userSetName.length) {
    return { full: userSetName, names: [], variant: 'user-set-name' };
  }

  if (!matrixClient) return fallback(room);

  const myUserId = matrixClient.getUserId();
  const joined = room.getJoinedMembers().filter((m) => !isSystemUser(m.userId));
  if (!joined.length) return fallback(room);

  if (joined.length === 1) {
    const [only] = joined;
    if (myUserId && only.userId === myUserId) {
      return { full: memberDisplayName(only), names: [], variant: 'current-user' };
    }
    return { full: memberDisplayName(only), names: [], variant: 'peer-name' };
  }

  const others = myUserId ? joined.filter((m) => m.userId !== myUserId) : joined;

  if (others.length === 0) return fallback(room);

  if (joined.length === 2 && others.length === 1) {
    return { full: memberDisplayName(others[0]), names: [], variant: 'peer-name' };
  }

  const names = others.map(memberDisplayName);
  return { full: names.join(', '), names, variant: 'member-list' };
};
